import { Box, Checkbox } from "@mui/material";
import {
  Cancel as CancelIcon,
  HelpOutline as HelpIcon,
  TrendingDown as TrendingDownIcon,
  Person as PersonIcon,
  ArrowDropUp,
  ArrowDropDown,
} from "@mui/icons-material";
import { t } from "./coachTokens";
import ClientAvatar from "./ClientAvatar";
import FilterChip from "./FilterChip";

export const avgColor = (avg, goal) => {
  if (avg == null) return t.t3;
  if (!goal) return t.text;
  return avg >= goal ? t.green : t.text;
};

export const trendStr = (trend) => {
  if (trend == null || trend === 0) return "—";
  const abs = Math.abs(trend).toLocaleString();
  return trend > 0 ? `↑ ${abs}` : `↓ ${abs}`;
};

export const trendColor = (trend) => {
  if (trend == null || trend === 0) return t.t3;
  return trend > 0 ? t.green : t.red;
};

export const StepsFilters = ({ filter, onChange }) => (
  <>
    <FilterChip
      active={filter === "belowGoal"}
      onClick={() => onChange(filter === "belowGoal" ? "all" : "belowGoal")}
      color={t.red}
      label={
        <>
          <CancelIcon sx={{ fontSize: 12 }} /> לא עמדו ביעד
        </>
      }
    />
    <FilterChip
      active={filter === "notLogged"}
      onClick={() => onChange(filter === "notLogged" ? "all" : "notLogged")}
      color={t.t2}
      label={
        <>
          <HelpIcon sx={{ fontSize: 12 }} /> לא תיעדו בכלל
        </>
      }
    />
    <FilterChip
      active={filter === "declining"}
      onClick={() => onChange(filter === "declining" ? "all" : "declining")}
      color={t.red}
      label={
        <>
          <TrendingDownIcon sx={{ fontSize: 12 }} /> ירידה לעומת שבוע שעבר
        </>
      }
    />
  </>
);

const cell = { padding: "12px 14px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", verticalAlign: "middle", textAlign: "center" };
const thBase = {
  textAlign: "center",
  padding: "10px 14px",
  fontSize: "10.5px",
  fontWeight: 700,
  color: t.t3,
  borderBottom: `1px solid ${t.border}`,
  background: t.bg,
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: ".06em",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const SortableTh = ({ label, sortKey, currentKey, currentDir, onSortChange, align = "center", width }) => {
  const active = currentKey === sortKey;
  return (
    <Box
      component="th"
      onClick={() => onSortChange(sortKey)}
      sx={{
        ...thBase,
        textAlign: align,
        width,
        cursor: "pointer",
        userSelect: "none",
        color: active ? t.text : t.t3,
        "&:hover": { color: t.text },
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", gap: "2px", justifyContent: align === "center" ? "center" : "flex-start" }}>
        {label}
        <Box sx={{ display: "inline-flex", flexDirection: "column", lineHeight: 0.6, opacity: active ? 1 : 0.35 }}>
          <ArrowDropUp sx={{ fontSize: 14, color: active && currentDir === "ASC" ? t.green : t.t3, mb: "-4px" }} />
          <ArrowDropDown sx={{ fontSize: 14, color: active && currentDir === "DESC" ? t.green : t.t3, mt: "-4px" }} />
        </Box>
      </Box>
    </Box>
  );
};

const NeutralPill = ({ label }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      background: t.bg,
      color: t.t3,
      fontSize: 11,
      fontWeight: 500,
      padding: "2px 10px",
      borderRadius: "20px",
      border: `1px dashed ${t.border}`,
    }}
  >
    {label}
  </Box>
);

const GoalPill = ({ avg, goal }) => {
  if (!goal) return <Box component="span" sx={{ color: t.t3 }}>–</Box>;
  if (avg == null) return <NeutralPill label="לא תיעד" />;
  const pct = Math.round((avg / goal) * 100);
  const met = avg >= goal;
  const bg = met ? t.greenLight : pct >= 80 ? t.bg : t.redLight;
  const color = met ? t.greenText : pct >= 80 ? t.text : t.redText;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        background: bg,
        color,
        fontSize: 11,
        fontWeight: 600,
        padding: "2px 10px",
        borderRadius: "20px",
      }}
    >
      {met ? `✓ ${pct}% מהיעד` : `${pct}% מהיעד`}
    </Box>
  );
};

const StepsTab = ({ rows, selected, onToggleSelect, onToggleAll, sortKey, sortDir, onSortChange, onOpenProfile, onOpenActions }) => {
  const allChecked = rows.length > 0 && rows.every((r) => selected.has(r.traineeId));

  if (rows.length === 0) {
    return (
      <Box sx={{ padding: "48px", textAlign: "center", color: t.t3, fontSize: "13px" }}>
        אין לקוחות להצגה
      </Box>
    );
  }

  return (
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <Box component="th" sx={{ ...thBase, width: 34 }}>
            <Checkbox
              size="small"
              checked={allChecked}
              onChange={(e) => onToggleAll(rows, e.target.checked)}
              sx={{ p: 0 }}
            />
          </Box>
          <SortableTh align="start" label="שם לקוח" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="ממוצע שבועי" sortKey="stepsAvg" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="שבוע שעבר" sortKey="stepsLastWeek" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <SortableTh label="מגמה" sortKey="stepsTrend" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
          <Box component="th" sx={thBase}>עמידה ביעד</Box>
          <Box component="th" sx={thBase}>פעולות</Box>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const isChecked = selected.has(r.traineeId);
          const hasAvg = r.stepsAvg != null && r.stepsAvg > 0;
          const hasLast = r.stepsLastWeek != null && r.stepsLastWeek > 0;
          return (
            <Box
              component="tr"
              key={r.traineeId}
              onClick={() => onOpenProfile?.(r)}
              sx={{
                cursor: "pointer",
                transition: "background .1s",
                "&:hover td": { background: t.greenLight },
              }}
            >
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  size="small"
                  checked={isChecked}
                  onChange={(e) => onToggleSelect(r.traineeId, e.target.checked)}
                  sx={{ p: 0 }}
                />
              </Box>
              <Box component="td" sx={{ ...cell, textAlign: "start" }}>
                <ClientAvatar name={r.traineeName} photoUrl={r.photoUrl} email={r.email} phone={r.phone} />
              </Box>
              <Box component="td" sx={cell}>
                {hasAvg ? (
                  <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
                    <Box component="strong" sx={{ color: avgColor(r.stepsAvg, r.stepsGoal) }}>
                      {r.stepsAvg.toLocaleString()}
                    </Box>
                    {r.stepsGoal ? (
                      <Box component="span" sx={{ color: t.t3, fontSize: 10.5 }}> / {r.stepsGoal.toLocaleString()}</Box>
                    ) : (
                      <Box component="span" sx={{ color: t.t3, fontSize: 10.5 }}> / -</Box>
                    )}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                {hasLast ? (
                  <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
                    <Box component="span" sx={{ color: avgColor(r.stepsLastWeek, r.stepsGoal) }}>
                      {r.stepsLastWeek.toLocaleString()}
                    </Box>
                    {r.stepsGoal ? (
                      <Box component="span" sx={{ color: t.t3, fontSize: 10.5 }}> / {r.stepsGoal.toLocaleString()}</Box>
                    ) : null}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: t.t3 }}>–</Box>
                )}
              </Box>
              <Box component="td" sx={cell}>
                <Box component="span" sx={{ color: trendColor(r.stepsTrend), fontWeight: 700 }}>
                  {trendStr(r.stepsTrend)}
                </Box>
              </Box>
              <Box component="td" sx={cell}>
                <GoalPill avg={r.stepsAvg} goal={r.stepsGoal} />
              </Box>
              <Box component="td" sx={cell} onClick={(e) => e.stopPropagation()}>
                <Box
                  component="button"
                  onClick={(e) => onOpenActions?.(e.currentTarget, r)}
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: "7px",
                    border: `1.5px solid ${t.border}`,
                    background: t.bg,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    color: t.t2,
                    "&:hover": { background: t.greenLight, borderColor: t.green, color: t.green },
                  }}
                >
                  <PersonIcon sx={{ fontSize: 14 }} />
                </Box>
              </Box>
            </Box>
          );
        })}
      </tbody>
    </Box>
  );
};

export default StepsTab;
